import * as React from 'react';
import { Venues, VendorsApi, CSNVenueProfile, CSNDiamondLevel, CSNServiceApi, CSNQueryParserResult, CSNGeographicPointFilter, CSNVenueSearchInput } from '@weddingspot/ws-api-client';
import { ConditionalVenueProfile } from 'components/venueProfile/types';
import { FeatureFlagContext, FLAG_VENUE_PROFILE_PAGE_REVIEW } from '@weddingspot/ws-core-components';
import { ApprovalStatus, REVIEW_BATCH_SIZE, SortOrder } from '../sections/Reviews/constant';
import { FLAG_ROOM_BLOCK_RECOMMENDATIONS_SHOWN_ON_PROFILE } from '@weddingspot/ws-core-components';
import { FLAG_ROOM_BLOCK_RECOMMENDATIONS_ENABLED } from '@weddingspot/ws-api-client';
import { Base64 } from 'js-base64';
import AppLogger from '../../../utils/logging';
const logger = AppLogger.createLogger();

export interface VenueProfileContextInterface {
    venueId: number | null;
    wsVenueProfile?: ConditionalVenueProfile;
    setWsVenueProfile: (venueDetails: ConditionalVenueProfile | undefined) => void;
    wsVenueProfileLoading: boolean;
    personalizedData?: Venues.VenueDetailsPersonalized;
    setPersonalizedData: (venueDetails: Venues.VenueDetailsPersonalized | undefined) => void;
    personalizedDataLoading: boolean;
    csnVenueProfile?: CSNVenueProfile;
    setCsnVenueProfile: (csnDetails: CSNVenueProfile | undefined) => void;
    csnVenueProfileLoading: boolean;
    profilePageSections?: any[];
    reset: () => void;
    reviews: Venues.OverallReviews;
    setReviews: (reviews: Venues.OverallReviews) => void;
    guestRoomsSearchRes?: any[];
    guestRoomsProfileRes?: any[];
    exploreHotelsURL?: string;
}

export const VenueProfileContext = React.createContext<VenueProfileContextInterface>({
    venueId: null,
    setWsVenueProfile: (venueDetails: ConditionalVenueProfile | undefined) => {},
    setPersonalizedData: (venueDetails: Venues.VenueDetailsPersonalized | undefined) => {},
    setCsnVenueProfile: (csnDetails: CSNVenueProfile | undefined) => {},
    personalizedDataLoading: false,
    csnVenueProfileLoading: false,
    wsVenueProfileLoading: false,
    reset: () => {},
    profilePageSections: [],
    reviews: { data: [] },
    setReviews: () => {},
    guestRoomsSearchRes: [],
    guestRoomsProfileRes: [],
    exploreHotelsURL: '',
});

interface VenueProfileContextProvideInterface {
    venueId: number | null;
    preloadWsVenueProfile?: Venues.VenueDetails;
    preloadVenueDetailsPersonalized?: Venues.VenueDetailsPersonalized;
    preloadCSNVenueProfile?: CSNVenueProfile;
}

export const VenueProfileContextProvider = <P extends VenueProfileContextProvideInterface>(props: React.PropsWithChildren<P>) => {
    const { venueId, preloadWsVenueProfile, preloadVenueDetailsPersonalized, preloadCSNVenueProfile } = props;
    const { isInitialized, flagEnabled } = React.useContext(FeatureFlagContext);
    const [wsVenueProfile, setWsVenueProfile] = React.useState<ConditionalVenueProfile | undefined>(preloadWsVenueProfile);
    const [wsVenueProfileLoading, setWsVenueProfileLoading] = React.useState<boolean>(false);
    const [personalizedData, setPersonalizedData] = React.useState<Venues.VenueDetailsPersonalized | undefined>(
        preloadVenueDetailsPersonalized
    );
    const [personalizedDataLoading, setPersonalizedDataLoading] = React.useState<boolean>(false);
    const [csnVenueProfile, setCsnVenueProfile] = React.useState<CSNVenueProfile | undefined>(preloadCSNVenueProfile);
    const [csnVenueProfileLoading, setCsnVenueProfileLoading] = React.useState<boolean>(false);

    const [reviews, setReviews] = React.useState<Venues.OverallReviews>({ data: [] });
    const [guestRoomsSearchRes, setGuestRoomsSearchRes] = React.useState<any[]>([]);
    const [guestRoomsProfileRes, setGuestRoomsProfileRes] = React.useState<any[]>([]);
    const [exploreHotelsURL, setExploreHotelsURL] = React.useState<string>();

    const [profilePageSections, setProfilePageSections] = React.useState([]);
    const sections = {
        highlight: { section: 'Highlights', shortTitle: 'Highlights', id: 'highlight' },
        amenities: { section: 'Amenities & Requirements', shortTitle: 'Amenities', id: 'amenities' },
        spaces: { section: 'Spaces', shortTitle: 'Spaces', id: 'spaces' },
        safety: { section: 'Safety', shortTitle: 'Safety', id: 'safety' },
        media: { section: 'Social Media', shortTitle: 'Media', id: 'media' },
        map: { section: 'Map', shortTitle: 'Map', id: 'map' },
        reviews: { section: 'Reviews', shortTitle: 'Reviews', id: 'reviews' },
        faq: { section: 'FAQ & Testimonials', shortTitle: 'FAQ', id: 'faq' },
        rooms: { section: 'Guest Rooms', shortTitle: 'Guest Rooms', id: 'rooms' },
    };

    React.useEffect(() => {
        if (venueId && !preloadWsVenueProfile) {
            setWsVenueProfileLoading(true);
            VendorsApi.venueDetails(venueId)
                .then((res) => {
                    setWsVenueProfile(res);
                    setWsVenueProfileLoading(false);
                })
                .catch((e) => {
                    logger.error(e);
                    setWsVenueProfileLoading(false);
                });
        }
    }, [venueId]);

    React.useEffect(() => {
        if (venueId && !preloadVenueDetailsPersonalized) {
            setPersonalizedDataLoading(true);
            VendorsApi.personalizedVenueDetails(venueId)
                .then((res) => {
                    setPersonalizedData(res);
                    setPersonalizedDataLoading(false);
                })
                .catch((e) => {
                    logger.error(e);
                    setPersonalizedDataLoading(false);
                });
        }
    }, [venueId]);

    const getGeoFilter = (obj: CSNQueryParserResult): CSNGeographicPointFilter => {
        /*
        CSNQueryParser checking if a search term matches a pre-defined GeoJSON polygon stored in Elasticsearch.
        if the Google Suggestion geographic type matches one of the polygon types(
            * US states,
            * Canadian provinces,
            * the Caribbean
        ),
        CSNQueryParser provides 'POLYGON' filter
        */
        const polygon = (obj?.additionalFilters || []).find((i) => i.type === 'POLYGON');
        if (polygon) {
            return polygon;
        }
        return {
            ...obj.geographicPointFilter,
            maximum: 25,
            measurementUnit: 'MILES',
        };
    };

    React.useEffect(() => {
        const venue = wsVenueProfile as Venues.VenueDetails;
        if (venue?.csn_venue_id && !preloadCSNVenueProfile) {
            setCsnVenueProfileLoading(true);
            CSNServiceApi.getVenueProfiles({ venueIds: [venue.csn_venue_id] })
                .then((res: CSNVenueProfile[]) => {
                    setCsnVenueProfile(res[0]);
                    setCsnVenueProfileLoading(false);
                })
                .catch((e) => {
                    setCsnVenueProfileLoading(false);
                });
        }
    }, [wsVenueProfile]);

    React.useEffect(() => {
        const showRoomBlockRecommendations = isInitialized && flagEnabled(FLAG_ROOM_BLOCK_RECOMMENDATIONS_SHOWN_ON_PROFILE);
        if (showRoomBlockRecommendations && FLAG_ROOM_BLOCK_RECOMMENDATIONS_ENABLED) {
            const venue = wsVenueProfile as Venues.VenueDetails;
            if(!wsVenueProfile.hide_guest_rooms && venue.address_1){
                CSNServiceApi.queryParser({
                    queryString: `${venue.address_1}, ${venue.city}, ${venue.state} ${venue.zip_code}`,
                }).then((queryParserRes: CSNQueryParserResult) => {
                    const geoFilter = getGeoFilter(queryParserRes);
                    const searchState = {
                        q: queryParserRes.formattedSearchText,
                        f: geoFilter,
                        b: queryParserRes.boundingBoxFilter.boundingBox,
                    };
                    const redirectUrl: string = `/hotels/search/?state=${Base64.btoa(JSON.stringify(searchState))}`;
                    setExploreHotelsURL(redirectUrl);
                    const requestPayload: CSNVenueSearchInput = {
                        query: queryParserRes.formattedSearchText,
                        geoFilter: geoFilter,
                        boundingBox: queryParserRes.boundingBoxFilter.boundingBox,
                        radius: 60,
                        pageFilter: {
                            from: 0,
                            size: 4,
                        },
                    };
                    CSNServiceApi.venuesSearch(requestPayload).then((searchRes) => { 
                        if (searchRes?.searchResult?.venueList?.length){
                            setGuestRoomsSearchRes(searchRes.searchResult.venueList);
                            CSNServiceApi.getVenueProfiles({
                                venueIds: searchRes.searchResult.venueList.map((v) => v.id),
                            }).then((profilesRes) => {
                                setGuestRoomsProfileRes(profilesRes);
                            });
                        }
                    });
                })
            }
        }
    }, [wsVenueProfile,isInitialized]);

    React.useEffect(() => {
        const venue = wsVenueProfile as Venues.VenueDetails;
        let profileSections = [sections.highlight];

        venue.amenities.length || venue.special_restrictions.length ? profileSections.push(sections.amenities) : null;

        if (csnVenueProfile && csnVenueProfile.basicProfile.socialTablesEnabled && csnVenueProfile.basicProfile.socialTablesProviderId) {
            profileSections.push(sections.spaces);
        }

        if (venue.pinterest_link && isInitialized && flagEnabled('pinterest_section_on_venue_profile_pages')) {
            profileSections.push(sections.media);
        }
        if (venue && venue.latitude && venue.longitude) {
            profileSections.push(sections.map);
        }

        if (venue && reviews.data.length > 0) {
            profileSections.push(sections.reviews);
        }

        // if (
        //     csnVenueProfile &&
        //     csnVenueProfile.guestRoom &&
        //     csnVenueProfile.basicProfile.diamondLevel !== CSNDiamondLevel.BASIC &&
        //     csnVenueProfile.basicProfile.diamondLevel !== CSNDiamondLevel.HALF_DIAMOND
        // ) {
        //     profileSections.push(sections.rooms);
        // }

        if (!wsVenueProfile.hide_guest_rooms && guestRoomsSearchRes?.length && guestRoomsProfileRes?.length){
            profileSections.push(sections.rooms);
        }

        /* @ts-ignore */
        setProfilePageSections([...profileSections]);
    }, [reviews, csnVenueProfile, isInitialized, guestRoomsSearchRes, guestRoomsProfileRes]);

    // fetch reviews only if allowed in admin section
    React.useEffect(() => {
        if (flagEnabled(FLAG_VENUE_PROFILE_PAGE_REVIEW) && venueId) {
            VendorsApi.getReviews(venueId, {
                sort_order: SortOrder.DESCENDING,
                size: REVIEW_BATCH_SIZE,
                offset: reviews.data.length,
                approval_statuses: ApprovalStatus.APPROVED,
            })
                .then((res: Venues.OverallReviews) => setReviews(res))
                .catch((e) => {
                    console.error(e);
                });
        }
    }, [venueId, isInitialized]);

    const reset = React.useCallback(() => {
        setWsVenueProfile(undefined);
        setPersonalizedData(undefined);
        setCsnVenueProfile(undefined);
        setProfilePageSections([]);
        setReviews({ data: [] });
    }, []);

    return (
        <VenueProfileContext.Provider
            value={{
                venueId,
                setWsVenueProfile,
                wsVenueProfile,
                wsVenueProfileLoading,
                personalizedData,
                setPersonalizedData,
                personalizedDataLoading,
                csnVenueProfile,
                setCsnVenueProfile,
                csnVenueProfileLoading,
                profilePageSections,
                reviews,
                setReviews,
                reset,
                guestRoomsSearchRes,
                guestRoomsProfileRes,
                exploreHotelsURL,
            }}
        >
            {props.children}
        </VenueProfileContext.Provider>
    );
};
